import zukeeper from 'zukeeper';
import {create} from 'zustand';

import {buildDefaultRecordData} from '../../pages/PCF/constants/pcfDefaultValues';
import type {
  PCFRecord,
  PCFRecordData,
  PCFStatus
} from '../../services/interface/pcfTypes';

const initialRecordData: PCFRecordData = buildDefaultRecordData();

export const resolveUserId = (value: unknown): string | null => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object' && '_id' in value) {
    const id = (value as {_id?: string})._id;

    return typeof id === 'string' ? id : null;
  }

  return null;
};

interface PCFStore {
  recordId: string | null;
  createdBy: string | null;
  recordData: PCFRecordData;
  status: PCFStatus;
  isSaving: boolean;
  setRecordFromApi: (record: PCFRecord) => void;
  patchRecordData: (partial: Partial<PCFRecordData>) => void;
  setIsSaving: (isSaving: boolean) => void;
  resetPCFStore: (defaultData: PCFRecordData) => void;
}

const usePCFStore = create<PCFStore>(
  zukeeper(
    (
      set: (
        fn: Partial<PCFStore> | ((state: PCFStore) => Partial<PCFStore>)
      ) => void
    ) => ({
      recordId: null,
      createdBy: null,
      recordData: initialRecordData,
      status: 'draft',
      isSaving: false,
      setRecordFromApi: (record: PCFRecord) => {
        set(() => ({
          recordId: record._id ?? null,
          createdBy: resolveUserId(record.createdBy),
          recordData: record.data,
          status: record.status
        }));
      },
      patchRecordData: (partial: Partial<PCFRecordData>) => {
        set((state: PCFStore) => ({
          recordData: {...state.recordData, ...partial}
        }));
      },
      setIsSaving: (isSaving: boolean) => {
        set(() => ({isSaving}));
      },
      resetPCFStore: (defaultData: PCFRecordData) => {
        set(() => ({
          recordId: null,
          createdBy: null,
          recordData: defaultData,
          status: 'draft',
          isSaving: false
        }));
      }
    })
  )
);

export default usePCFStore;
